/**
 * Forgot Password Screen
 * HIGH Priority: Complete password reset flow
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaCheckCircle, FaEnvelope } from "react-icons/fa";
import { Link } from "react-router-dom";
import { forgotPassword } from "../api/modules/auth.api";
import { FloatingActionButtons } from "../components/ui/FloatingActionButtons";
import { ROUTES } from "../constants/routes";

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await forgotPassword(email);

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || "Failed to send reset email");
      }
    } catch (err: any) {
      setError(err.message || "Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
        <div className="max-w-md w-full card-professional rounded-2xl p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full">
            <FaCheckCircle className="text-green-600 text-3xl" />
          </div>

          <h2 className="mt-6 text-2xl font-bold text-[var(--text-primary)] text-center">
            {t("auth.forgotPassword.resetLinkSent")}
          </h2>

          <p className="mt-3 text-sm text-[var(--text-secondary)] text-center">
            {t("auth.forgotPassword.checkEmail")}{" "}
            <span className="font-semibold text-[var(--text-primary)]">
              {email}
            </span>
          </p>

          <Link
            to={ROUTES.LOGIN}
            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            <FaArrowLeft />
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)] px-4">
      <div className="max-w-md w-full card-professional rounded-2xl p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full">
          <FaEnvelope className="text-primary text-3xl" />
        </div>

        <h2 className="mt-6 text-2xl font-bold text-[var(--text-primary)] text-center">
          {t("auth.forgotPassword.title")}
        </h2>

        <p className="mt-2 text-sm text-[var(--text-secondary)] text-center">
          {t("auth.forgotPassword.subtitle")}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              {t("auth.forgotPassword.email")}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t("auth.forgotPassword.emailPlaceholder")}
              className="w-full px-4 py-3 border border-[var(--border-light)] bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loading
              ? t("auth.forgotPassword.sending")
              : t("auth.forgotPassword.sendResetLink")}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={ROUTES.LOGIN}
            className="text-sm text-primary hover:text-pink-700 font-medium"
          >
            <FaArrowLeft className="inline mr-2" />
            {t("auth.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default ForgotPassword;
